import { component } from "picoapp";
import { PRODUCT_CARD_MAIN_SETTINGS } from "../../sections/Carousels/tbsp-carousel--constants";
import { updateMaestroVariants } from "../../utilities";

export const productCardCarousel = component(
  async (node: HTMLElement, context) => {
    const hasOnlyDefaultVariant = node.dataset.hasOnlyDefaultVariant === "true";

    if (hasOnlyDefaultVariant) return;

    const [{ default: Splide }] = await Promise.all([
      import("@splidejs/splide"),
      // @ts-ignore
      import("@splidejs/splide/css/core"),
    ]);

    const imageSlider: HTMLElement = node.querySelector("aside");
    let start = 0;
    const initialSlide: HTMLElement = imageSlider.querySelector(
      `.splide__slide[data-selected]`
    );
    if (initialSlide) start = parseInt(initialSlide.dataset.index);

    const images = new Splide(imageSlider, {
      ...PRODUCT_CARD_MAIN_SETTINGS,
      start,
    });

    // Price
    images.on("move", function (newSlideIndex) {
      const prices = node.querySelectorAll("[data-prices]");
      prices.forEach((price: HTMLElement) => (price.style.display = "none"));
      const activePriceElement: HTMLElement = node.querySelector(
        `[data-prices][data-index="${newSlideIndex}"]`
      );
      activePriceElement.style.display = "flex";
    });

    images.on("visible", ({ slide }) => {
      slide.classList.remove("absolute", "invisible");
    });

    images.mount();

    // Options
    const breakpoints = {
      md: window.matchMedia("(min-width: 768px)"),
      mg: window.matchMedia("(min-width: 1024px)"),
      lg: window.matchMedia("(min-width: 1280px)"),
      xl: window.matchMedia("(min-width: 1440px)"),
    };

    const initThumbnailSlider = () => {
      const options = node.querySelector("nav");
      if (!options) return;
      let padding = parseInt(options.dataset.padding);
      let perPage = parseInt(options.dataset.perPage);
      let gap = parseInt(options.dataset.gap);

      Object.keys(breakpoints).forEach((breakpointKey) => {
        const mediaQuery = breakpoints[breakpointKey];

        if (mediaQuery.matches) {
          const paddingData = options.dataset[`${breakpointKey}Padding`];
          padding = paddingData ? parseInt(paddingData) : padding;
          const perPageData = options.dataset[`${breakpointKey}PerPage`];
          perPage = perPageData ? parseInt(perPageData) : perPage;
          const gapData = options.dataset[`${breakpointKey}Gap`];
          gap = gapData ? parseInt(gapData) : gap;
        }
      });

      let page = 0;

      const movePage = (newPage: number | undefined) => {
        if (newPage === undefined) return page;
        page = newPage;
        return page;
      };

      const track = options.querySelector("[data-track]");
      const list = options.querySelector("[data-list]");
      if (!track || !list) return;
      const slides = options.querySelectorAll("[data-slide]");
      const arrows = options.querySelector("[data-arrows]");
      const prevArrow = options.querySelector("[data-prev]");
      const nextArrow = options.querySelector("[data-next]");

      const groupedSlides = groupSlides(Array.from(slides), perPage);
      const thumbnailElements = createThumbnailElements(
        groupedSlides,
        prevArrow,
        {
          gap,
          perPage,
          padding,
          movePage,
        }
      );
      buildThumbnailSlider(
        thumbnailElements,
        images,
        track,
        list,
        prevArrow,
        nextArrow,
        {
          start,
          padding,
          gap,
          perPage,
          slidesWrapper: node,
          movePage,
        }
      );
      updateArrows(arrows, prevArrow, nextArrow, groupedSlides, list, {
        padding,
        movePage,
      });

      if (options.classList.contains("opacity-0")) {
        options.classList.replace("opacity-0", "opacity-100");
      }
    };

    initThumbnailSlider();
    Object.keys(breakpoints).forEach((breakpointKey) => {
      const mediaQuery = breakpoints[breakpointKey];
      mediaQuery.addEventListener("change", function (e: MediaQueryListEvent) {
        initThumbnailSlider();
      });
    });

    context.on("tbsp:variant-selected", async (variant) => {
      const matchingOption: HTMLDivElement = node.querySelector(
        `nav [data-product-variant-title="${variant.title}"]`
      );

      if (matchingOption) {
        matchingOption.dispatchEvent(new Event("mouseenter"));
      }
    });
  }
);

const groupSlides = (slides: Element[], perPage: number): HTMLElement[][] => {
  let index = 0;
  let slidesPerGroup = perPage;
  const groupedSlides = slides.reduce((group, slide, slideIndex, slides) => {
    if (index % slidesPerGroup === 0) {
      group.push([slide]);
      if (slides.length - (slideIndex - 1) < perPage) {
        slidesPerGroup = perPage;
      } else if (group.length > 1) {
        slidesPerGroup = perPage - 1;
      }
      index = 0;
    } else {
      group[group.length - 1].push(slide);
    }
    index++;
    return group;
  }, []);

  return groupedSlides;
};

// eslint-disable-next-line no-undef
type SlideWrapper = keyof HTMLElementTagNameMap;
type SliderOptions = {
  start?: number;
  padding?: number;
  gap?: number;
  perPage?: number;
  slideWrapper?: SlideWrapper;
  slidesWrapper?: HTMLElement | Document;
  movePage?: (page?: number) => number;
};
type Arrow = Element | HTMLElement;

const createThumbnailElements = (
  groupedSlides: HTMLElement[][],
  _prevArrow: Arrow,
  sliderOptions: SliderOptions
): HTMLElement[][] => {
  const createEmptyArrowContainer = (prev: boolean): HTMLElement => {
    const emptyDiv = document.createElement("div");
    emptyDiv.classList.add("h-full", "flex");
    emptyDiv.style.width = `calc(((100% + ${sliderOptions.gap}px) / ${sliderOptions.perPage}) - ${sliderOptions.gap}px)`;

    return emptyDiv;
  };

  const groupedSlidesWithArrows = groupedSlides.map((slides, index) => {
    const prevArrow = createEmptyArrowContainer(true);
    const nextArrow = createEmptyArrowContainer(false);

    if (!index && groupedSlides.length === 1) {
      return [...slides];
    } else if (!index) {
      return [...slides, nextArrow];
    } else if (index === groupedSlides.length - 1) {
      return [prevArrow, ...slides];
    } else {
      return [prevArrow, ...slides, nextArrow];
    }
  });

  return groupedSlidesWithArrows;
};

const buildThumbnailSlider = (
  groupedSlides: HTMLElement[][],
  MainSlider,
  track: Element,
  list: Element,
  prevArrow: Arrow,
  nextArrow: Arrow,
  {
    start = 0,
    padding,
    gap,
    perPage,
    slideWrapper = "div",
    slidesWrapper = document,
    movePage,
  }: SliderOptions
) => {
  let currentPage = movePage();
  list.innerHTML = "";
  (list as HTMLElement).style.marginRight = `-${padding}px`;
  (track as HTMLElement).style.padding = `${padding}px`;
  groupedSlides.forEach((slides, pageIndex) => {
    // build slides container per page
    const pageContainer = createSlidesPageContainer(slideWrapper, padding);
    slides.forEach((slide: HTMLElement, index) => {
      if (index !== slides.length - 1) {
        slide.style.marginRight = `${gap}px`;
      }

      slide.setAttribute("data-page-index", `${pageIndex}`);
      slide.setAttribute("aria-hidden", "true");
      if (pageIndex > 0 && pageIndex !== groupedSlides.length - 1) {
        slide.style.display = "flex";
        slide.style.justifyContent = "center";
      } else {
        slide.style.display = "block";
      }

      const isSlide = slide.hasAttribute("data-slide");
      if (isSlide) {
        const newPageIndex = updateThumbnail(
          slide,
          MainSlider,
          index,
          pageIndex,
          {
            start,
            padding,
            gap,
            perPage,
            slidesWrapper,
          }
        );

        currentPage = movePage(newPageIndex);
      }

      pageContainer.appendChild(slide);
    });

    if (currentPage === pageIndex) {
      slides.forEach((_slide: HTMLElement) => {
        _slide.setAttribute("aria-hidden", "false");
      });
    }
    list.appendChild(pageContainer);
  });

  moveSlider(
    false,
    prevArrow,
    nextArrow,
    list,
    currentPage,
    groupedSlides.length - 1,
    { padding }
  );
};

const createSlidesPageContainer = (tag: SlideWrapper, padding): HTMLElement => {
  const slidesContainerClasses = "flex w-full shrink-0 m-0 relative box-border";
  const slidesContainer = document.createElement(tag);
  slidesContainer.classList.add(...slidesContainerClasses.split(" "));
  slidesContainer.style.marginRight = `${padding}px`;
  return slidesContainer;
};

const updateThumbnail = (
  slide: HTMLElement,
  MainSlider,
  index: number,
  pageIndex: number,
  sliderOptions: SliderOptions
): number => {
  let newPageIndex;
  const slideIndex = parseInt(slide.dataset.index);
  const mainImageSlide = MainSlider.Components.Slides.getAt(index);
  slide.style.width = `calc(((100% + ${sliderOptions.gap}px) / ${sliderOptions.perPage}) - ${sliderOptions.gap}px)`;
  slide.setAttribute("aria-label", `Go to slide ${index + 1}`);
  slide.setAttribute("aria-controls", mainImageSlide.slide.id);
  if (slideIndex === sliderOptions.start) {
    slide.setAttribute("aria-current", "true");
    newPageIndex = pageIndex;
  } else {
    slide.setAttribute("aria-current", "false");
  }

  const selectOptionSlide = (e) => {
    const swatch = e.target;
    const productCard = swatch.closest("[data-product-current-variant-id]");

    if (productCard) {
      updateMaestroVariants(productCard, swatch);
      productCard.dataset.productCurrentVariantId =
        swatch.dataset.productVariantId;
    }

    const selectedThumbnail = selectThumbnail(
      slide,
      slideIndex,
      MainSlider,
      sliderOptions.slidesWrapper
    );

    return selectedThumbnail();
  };

  slide
    .querySelector("[data-thumbnail]")
    .addEventListener("mouseenter", selectOptionSlide);
  slide
    .querySelector("[data-thumbnail]")
    .addEventListener("click", selectOptionSlide);
  return newPageIndex;
};

const updateArrows = (
  arrows: Element,
  prevArrow: Arrow,
  nextArrow: Arrow,
  groupedSlides: HTMLElement[][],
  list,
  sliderOptions: SliderOptions
) => {
  (arrows as HTMLElement).style.marginRight = `${sliderOptions.padding}px`;
  (arrows as HTMLElement).style.marginLeft = `${sliderOptions.padding}px`;
  const arrowClickEvent = (prev: boolean) => () => {
    const currentPage = sliderOptions.movePage();
    if (prev) {
      const newPage = sliderOptions.movePage(currentPage - 1);
      moveSlider(
        prev,
        prevArrow,
        nextArrow,
        list,
        newPage,
        groupedSlides.length - 1,
        sliderOptions
      );
    } else {
      const newPage = sliderOptions.movePage(currentPage + 1);
      moveSlider(
        prev,
        prevArrow,
        nextArrow,
        list,
        newPage,
        groupedSlides.length - 1,
        sliderOptions
      );
    }
  };

  const handlePrevArrow = arrowClickEvent(true);
  const handleNextArrow = arrowClickEvent(false);

  prevArrow.addEventListener("click", handlePrevArrow);
  nextArrow.addEventListener("click", handleNextArrow);
};

const selectThumbnail =
  (
    slide: Element,
    slideIndex: number,
    MainSlider,
    wrapper: HTMLElement | Document
  ) =>
  () => {
    wrapper.querySelectorAll("[data-slide]").forEach((_slide) => {
      _slide.setAttribute("aria-current", "false");
    });
    slide.setAttribute("aria-current", "true");
    MainSlider.go(slideIndex);
  };

const moveSlider = (
  prev: boolean,
  prevArrow: Arrow,
  nextArrow: Arrow,
  list: Element,
  pageIndex: number,
  maxPage: number,
  sliderOptions: SliderOptions
) => {
  const { width } = list.getBoundingClientRect();
  const toggleSlidesInteractive = () => {
    list
      .querySelectorAll("[aria-hidden='false']")
      .forEach((visibleSlide: HTMLElement) => {
        visibleSlide.setAttribute("aria-hidden", "true");
      });
    list
      .querySelectorAll(`[data-page-index="${pageIndex}"]`)
      .forEach((newVisibleSlides: HTMLElement) => {
        newVisibleSlides.setAttribute("aria-hidden", "false");
      });
  };

  const containerWidth = width + sliderOptions.padding;
  const translateValue = containerWidth * pageIndex;
  if (prev) {
    (list as HTMLElement).style.transform = `translateX(-${translateValue}px)`;
    toggleSlidesInteractive();
  } else {
    (list as HTMLElement).style.transform = `translateX(-${translateValue}px)`;
    toggleSlidesInteractive();
  }

  if (!pageIndex) {
    toggleArrow(prevArrow, false);
    if (maxPage > pageIndex) {
      toggleArrow(nextArrow, true);
    } else {
      toggleArrow(nextArrow, false);
    }
  } else if (pageIndex === maxPage && maxPage !== 0) {
    toggleArrow(prevArrow, true);
    toggleArrow(nextArrow, false);
  } else {
    toggleArrow(prevArrow, true);
    toggleArrow(nextArrow, true);
  }
};

const toggleArrow = (arrow: Arrow, show: boolean) => {
  if (!show) {
    arrow.setAttribute("aria-hidden", "true");
    arrow.setAttribute("disabled", "");
    (arrow as HTMLElement).style.display = "none";
  } else {
    arrow.setAttribute("aria-hidden", "false");
    arrow.removeAttribute("disabled");
    (arrow as HTMLElement).style.display = "flex";
  }
};
